<script context="module" lang="ts">
  export var id = 0;
  export const getId = (name: string) => {
    id += 1;
    return `${name} ${id}`;
  };

  export const defaultComponentContainerSettings: TComponentContainerSettings =
    {
      backgroundColor: "",
      backgroundImage: "",
      backgroundOpacity: 1.0,
      badge: false,
      bleed: false,
      bleedBackground: false,
      bleedBottom: false,
      blur: false,
      card: false,
      cardRadius: "16px",
      cover: false,
      extraLightBleed: false,
      fullHeight: false,
      fullWidth: false,
      heavyPadding: false,
      height: "auto",
      inset: false,
      light: false,
      lightBleed: false,
      lightMargin: false,
      lightPadding: false,
      mediumMargin: false,
      noBackground: false,
      noMargin: false,
      noPadding: false,
      noTopPadding: false,
      showDivider: false,
      skipTag: false,
      style: "",
      textAlign: "center",
      textColor: "",
      width: "100%",
    };
</script>

<script lang="ts">
  import DividingHeader from "../DividingHeader/DividingHeader.svelte";
  import { getContext } from "svelte";
  import { setContext } from "svelte";

  import type {
    TComponentContainerSettings,
    TComponentContainerProps,
    TTag,
  } from "./types";

  interface $$Props extends TComponentContainerProps {}

  export let id: string;
  export let tag: TTag = "Container";
  export let settings: Partial<TComponentContainerSettings> | {} = {};
  const _settings: TComponentContainerSettings = {
    ...defaultComponentContainerSettings,
    ...settings,
  };

  /**
   * This is a guard against our old component ids that were not prefixed with __sw-
   */
  if (!id?.startsWith("__sw-")) {
    id = null;
  }

  export var onclick = () => {};

  let prefix = getContext("prefixContext") ?? "";
  let contextSettings = getContext("containerContext");
  if (contextSettings) {
    // TODO fix this
    // console.log("contextSettings", tag, contextSettings, settings);
    // @ts-ignore
    for (const key in contextSettings) {
      // if (!(key in options)) {
      _settings[key] = contextSettings[key];
      // }

      // if (key in options) {
      //   if (options[key] != defaultComponentContainerSettings[key]) {
      //     _settings[key] = options[key];
      //   }
      // }
    }
  }
  setContext("containerContext", {});

  const color = _settings.textColor || "inherit";
  const width = _settings.fullWidth
    ? "calc(100% + var(--sw-container-padding) * 2)"
    : _settings.width;
  const textAlign = _settings.textAlign;
  const position = "relative";
  const backgroundColor = _settings.backgroundColor
    ? `background-color: ${_settings.backgroundColor};`
    : "";
  const height = _settings.height;
  const borderRadius = _settings.cardRadius;
  const restStyle = _settings.style ? `${_settings.style};` : "";

  const compiledStyles = `
    color: ${color};
    width: ${width};
    text-align: ${textAlign};
    position: ${position};
    ${backgroundColor}
    height: ${height};
    border-radius: ${borderRadius};
    ${restStyle}
  `;
</script>

{#if _settings.showDivider}
  <DividingHeader settings={{ title: tag }} />
{/if}

<div
  on:click={onclick}
  data-pw-namespace={_settings.skipTag ? null : `${tag}${id ? ` ${id}` : ""}`}
  class:full-width={_settings.fullWidth}
  class:full-height={_settings.fullHeight}
  class:card={_settings.card}
  class:blur={_settings.blur}
  class:light={_settings.light}
  class:light-margin={_settings.lightMargin}
  class:medium-margin={_settings.mediumMargin}
  class:no-margin={_settings.noMargin}
  class:cover={_settings.cover}
  class:inset={_settings.inset}
  class:no-padding={_settings.noPadding}
  class:no-top-padding={_settings.noTopPadding}
  class:light-padding={_settings.lightPadding}
  class:heavy-padding={_settings.heavyPadding}
  class:no-bg={_settings.noBackground}
  class:bleed={_settings.bleed}
  class:lightBleed={_settings.lightBleed}
  class:extraLightBleed={_settings.extraLightBleed}
  class:bleedBottom={_settings.bleedBottom}
  style={compiledStyles}
>
  {#if _settings.backgroundImage}
    <div
      class="background-image {_settings.bleedBackground ? 'bleed' : ''}"
      style="background-image: url('../public/assets/{_settings.backgroundImage}'); background-size: cover; background-position: 50% 50%; height: {_settings.height}; margin-bottom: 0px; opacity: {_settings.backgroundOpacity};"
    />
  {/if}
  {#if _settings.badge}
    <p class="badge">Badge Text</p>
  {/if}

  <slot {prefix} />
</div>

<style>
  div.full-height {
    height: var(--sw-safe-area-height);
  }

  .background-image {
    position: absolute;
    width: calc(100%);
    top: 50%;
    left: 50%;
    z-index: -1;
    transform: translate(-50%, -50%);
  }

  div {
    margin-bottom: calc(var(--sw-container-padding) * 2);
    flex-shrink: 0 !important;
    scroll-snap-align: center;
  }

  div.light-margin {
    margin-bottom: calc(var(--sw-spacing));
  }

  div.medium-margin {
    margin-bottom: calc(var(--sw-container-padding));
  }

  div.no-margin {
    margin-bottom: 0px;
  }

  .full-width {
    position: relative;
    width: calc(100% + var(--sw-container-padding) * 2);
    left: 50%;
    transform: translate(-50%, 0);
  }

  .cover {
    background-color: var(--sw-foreground-color-7);
    position: relative;
    padding: calc(var(--sw-container-padding) * 1.6)
      calc(var(--sw-container-padding) * 1.6);
    width: calc(100% + var(--sw-container-padding) * 2);
    left: 50%;
    transform: translate(-50%, 0);
  }

  .card {
    background-color: var(--sw-foreground-color-7);
    padding: calc(var(--sw-container-padding) * 1.6)
      calc(var(--sw-container-padding) * 1.6);
  }

  .card.blur {
    backdrop-filter: blur(15px);
    -webkit-backdrop-filter: blur(15px);
  }

  .inset {
    padding-left: calc(var(--sw-container-padding) * 1.6);
    padding-right: calc(var(--sw-container-padding) * 1.6);
  }

  .card.light,
  .cover.light {
    background-color: rgba(255, 255, 255, 0.05);
  }

  .no-padding {
    padding: 0px;
  }

  .no-top-padding {
    padding-top: 0px;
  }

  .card.no-padding {
    overflow: hidden;
  }

  .no-bg {
    background-color: rgba(0, 0, 0, 0);
  }

  .badge {
    position: absolute;
    top: 0;
    right: 0;
    font-size: calc(var(--sw-font-size) * 0.9);
    font-weight: 500;
    transform: translate(calc(-1 * var(--sw-container-padding)), -50%);
    padding: 4px 15px;
    background-color: var(--sw-brand-color);

    border-radius: 20px;
  }

  .light-padding {
    padding: var(--sw-container-padding) calc(var(--sw-container-padding) * 1.2);
  }

  .heavy-padding {
    padding-left: calc(var(--sw-container-padding) * 1.5);
    padding-right: calc(var(--sw-container-padding) * 1.5);
  }

  .bleed {
    -webkit-mask-image: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0),
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 0)
    );
    mask-image: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0),
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 0)
    );
  }

  .lightBleed {
    -webkit-mask-image: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0),
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 0)
    );
    mask-image: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0),
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 0)
    );
  }

  .extraLightBleed {
    -webkit-mask-image: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0),
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 0)
    );
    mask-image: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0),
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 0)
    );
  }

  .bleedBottom {
    -webkit-mask-image: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 0)
    );
    mask-image: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 0)
    );
  }
</style>
